import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';

const normalizeItem = (item) => {
  const date = item.feedBack_date ? new Date(item.feedBack_date) : null;
  const dueDate = item.feedback_due_date ? new Date(item.feedback_due_date) : null;

  return {
    ...item,
    status: item.status ? item.status.toLowerCase() : 'pending',
    formattedDate: date ? date.toLocaleDateString() : 'NA',
    formattedDueDate: dueDate ? dueDate.toLocaleDateString() : null,
  };
};

const FeedbackLinks = ({ ...rest }) => {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    fetch(`${process.env.REACT_APP_API_URL}/api/gettraineefeedbackdetail/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!isMounted) return;
        if (!data.detail && Array.isArray(data)) {
          setFeedbackItems(data.map((item) => normalizeItem(item)));
        } else {
          setFeedbackItems([]);
        }
      })
      .catch(() => {
        setFeedbackItems([]);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleNavigate = useCallback(
    ({ feedBack_date, feedback_no, lab_type, feedback_id, trainee_id, id, name }) => {
      localStorage.setItem('feedbackDate', feedBack_date);
      localStorage.setItem('feedbackno', feedback_no);
      localStorage.setItem('labtype', lab_type);
      localStorage.setItem('feedbackid', feedback_id);
      localStorage.setItem('traineeid', trainee_id);
      localStorage.setItem('InstanceID', id);
      localStorage.setItem('feedbackLabName', name);
      navigate('/app/submitfeedback/');
    },
    [navigate]
  );

  const traineeName = localStorage.getItem('user') || 'NA';
  const labName = feedbackItems.length ? feedbackItems[0].name : 'NA';

  const stats = useMemo(() => {
    const totals = feedbackItems.reduce(
      (acc, item) => {
        if (item.status === 'completed' || item.status === 'submitted') {
          acc.completed += 1;
        } else {
          acc.pending += 1;
        }
        return acc;
      },
      { completed: 0, pending: 0 }
    );
    totals.total = feedbackItems.length;
    return totals;
  }, [feedbackItems]);

  const filteredItems = useMemo(() => {
    if (statusFilter === 'all') return feedbackItems;
    return feedbackItems.filter((item) => {
      const status = item.status;
      if (statusFilter === 'pending') {
        return status !== 'completed' && status !== 'submitted';
      }
      return status === 'completed' || status === 'submitted';
    });
  }, [feedbackItems, statusFilter]);

  const renderSkeleton = useMemo(
    () => (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={index}>
            <Card variant="outlined">
              <CardContent>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="rectangular" height={20} sx={{ mt: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={20} sx={{ mt: 1, borderRadius: 1 }} />
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: 18 }} />
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    ),
    []
  );

  const renderEmptyState = useMemo(
    () => (
      <Box
        sx={{
          mt: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <SentimentSatisfiedAltIcon sx={{ fontSize: 64, mb: 1 }} />
        <Typography variant="h6">You’re all caught up!</Typography>
        <Typography variant="body2" align="center" sx={{ maxWidth: 360 }}>
          We’ll let you know as soon as a new feedback form is ready for you.
        </Typography>
      </Box>
    ),
    []
  );

  const renderCards = useMemo(
    () => (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {filteredItems.map((item) => {
          const isPending = item.status !== 'completed' && item.status !== 'submitted';
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.feedback_id || item.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: isPending ? 'primary.light' : 'divider',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Stack spacing={1.25}>
                    <Typography variant="h6">{item.feedback_name}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        label={item.lab_type || 'Lab'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={isPending ? 'Pending' : 'Completed'}
                        size="small"
                        color={isPending ? 'warning' : 'success'}
                      />
                      {item.formattedDueDate && (
                        <Chip
                          label={`Due: ${item.formattedDueDate}`}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </Stack>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Feedback date: {item.formattedDate}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Feedback # {item.feedback_no}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
                <CardActions sx={{ px: 2.5, pb: 2.5 }}>
        <Button
                    fullWidth
          variant="contained"
                    color={isPending ? 'primary' : 'success'}
                    onClick={() => handleNavigate(item)}
                  >
                    {isPending ? 'Start feedback' : 'View submission'}
        </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    ),
    [filteredItems, handleNavigate]
  );

  return (
    <Container maxWidth={false} sx={{ pt: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
      <Card {...rest} sx={{ mb: 4 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center">
                <SchoolIcon color="primary" />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Lab name
        </Typography>
                  <Typography variant="h5">{labName}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Trainee
            </Typography>
                    <Typography variant="h6">{traineeName}</Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
        </Grid>
        </Box>

        <Divider />

        <Grid container spacing={2} sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={1} alignItems="flex-start">
                  <AssignmentTurnedInIcon color="primary" fontSize="large" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Feedback assigned
            </Typography>
                  <Typography variant="h4">{stats.total}</Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip label={`${stats.pending} pending`} color="warning" size="small" />
                    <Chip label={`${stats.completed} completed`} color="success" size="small" />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={8}>
            <Stack spacing={2}>
              <Typography variant="subtitle2">Filter feedback</Typography>
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(_, value) => value && setStatusFilter(value)}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="pending">Pending</ToggleButton>
                <ToggleButton value="completed">Completed</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
        </Grid>
        </Grid>

        <Divider />

        <Box sx={{ px: { xs: 2.5, md: 3 }, py: 3 }}>
          {loading ? renderSkeleton : filteredItems.length ? renderCards : renderEmptyState}
        </Box>
    </Card>
    </Container>
  );
};

export default FeedbackLinks;
